// frontend/src/hooks/useUsage.ts
//
// Hook for fetching and tracking credit-based usage.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface UsageData {
  tier: string;
  credits: {
    subscriptionRemaining: number;
    subscriptionTotal: number;
    paygRemaining: number;
    paygExpiresAt: string | null;
    totalRemaining: number;
  };
  costs: {
    sponsoredAiCall: number;
    builderAiPer30Sec: number;
  };
  canMakeSponsoredAiCall: boolean;
  canMakeBuilderAiCall: boolean;
  storage: {
    bytesUsed: number;
    bytesLimit: number;
    bytesRemaining: number;
  };
  // Legacy compat
  canMakeAiCall: boolean;
  storageRemaining: number;
}

interface UseUsageReturn {
  data: UsageData | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Cache usage data briefly to avoid excessive API calls
let cachedData: UsageData | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 30 * 1000; // 30 seconds

// Simple event mechanism to notify all active useUsage hooks to refetch
let invalidationVersion = 0;

/** Invalidate the usage cache and trigger a refetch in all active useUsage hooks */
export function invalidateUsageCache() {
  cachedData = null;
  cacheTimestamp = 0;
  invalidationVersion++;
}

export function useUsage(): UseUsageReturn {
  const { session, user, authReady, ensureFreshToken } = useAuth();
  const [data, setData] = useState<UsageData | null>(cachedData);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(invalidationVersion);

  // Poll for cache invalidation
  useEffect(() => {
    const interval = setInterval(() => {
      if (invalidationVersion !== version) {
        setVersion(invalidationVersion);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [version]);

  const fetchUsage = useCallback(async (force = false) => {
    if (!authReady) {
      return;
    }

    if (!session?.access_token || !user) {
      setData(null);
      setLoading(false);
      return;
    }

    // Use cache if valid
    if (!force && cachedData && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      let token = await ensureFreshToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const request = async (accessToken: string) => fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-usage`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey,
          },
        }
      );

      let response = await request(token);
      if (response.status === 401) {
        const refreshedToken = await ensureFreshToken({ forceRefresh: true });
        if (refreshedToken) {
          token = refreshedToken;
          response = await request(token);
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch usage data (HTTP ${response.status})`);
      }

      const usageData = await response.json();

      // Update cache
      cachedData = usageData;
      cacheTimestamp = Date.now();

      setData(usageData);
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [session, user, authReady, ensureFreshToken]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage, version]);

  const refetch = useCallback(async () => {
    await fetchUsage(true);
  }, [fetchUsage]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Calculate percentage used
 */
export function getUsagePercent(used: number, limit: number): number {
  if (limit === 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}
